// frontend/src/pages/asset-admin/AssetAdminCustodians.js
/**
 * Asset Admin – Custodian Management
 *
 * HR Admins can assign / revoke asset custodian categories for any staff
 * member without needing Django Admin access.
 *
 * Theme  : Red (Admin)
 * Layout : Mobile-first grid/list toggle matching PendingReturns.js pattern
 *
 * @see frontend/src/styles/design-system-reference.md
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Shield, Plus, Edit2, UserMinus, Search, Grid3X3, List,
  AlertTriangle, X, CheckCircle, Laptop, Car, FileKey,
  Briefcase, DoorOpen, CreditCard, Fuel, Smartphone, Package, Loader2,
} from 'lucide-react';
import toast from '../../utils/toast';
import Modal from '../../components/UI/Modal';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import ConfirmModal from '../../components/UI/ConfirmModal';
import { CountBadge } from '../../components/UI/CountBadge';
import PillToggle from '../../components/UI/PillToggle';
import { usePersistence } from '../../hooks/usePersistence';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  useCustodians,
  useUpdateCustodianCategories,
  useAssetCategories,
} from '../../hooks/useAssetInventoryData';
import { staffAPI } from '../../services/api';

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const CATEGORY_ICONS = {
  IT: Laptop, ACC: DoorOpen, FLT: Car,
  LIC: FileKey, OFF: Briefcase, FUEL: CreditCard, DSL: Fuel, TEL: Smartphone,
};

const CATEGORY_CHIP_COLORS = {
  blue:   'bg-blue-100 text-blue-700',
  green:  'bg-green-100 text-green-700',
  purple: 'bg-purple-100 text-purple-700',
  orange: 'bg-orange-100 text-orange-700',
  red:    'bg-red-100 text-red-700',
  gray:   'bg-gray-100 text-gray-700',
  sky:    'bg-sky-100 text-sky-700',
};

const CategoryIcon = ({ code, className = 'w-4 h-4' }) => {
  const Icon = CATEGORY_ICONS[code] || Package;
  return <Icon className={className} />;
};

const CategoryChip = ({ category }) => {
  const colorClass = CATEGORY_CHIP_COLORS[category.color] || CATEGORY_CHIP_COLORS.gray;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${colorClass}`}>
      <CategoryIcon code={category.code} className="w-3 h-3" />
      {category.name}
    </span>
  );
};

const getInitials = (firstName, lastName) =>
  `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();

// ─────────────────────────────────────────────
// SKELETON
// ─────────────────────────────────────────────

const GridSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 animate-pulse flex flex-col h-full gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
        </div>
        <div className="flex gap-1.5 flex-1">
          <div className="h-5 bg-gray-200 rounded-full w-14" />
          <div className="h-5 bg-gray-200 rounded-full w-10" />
        </div>
        <div className="card-action-footer">
          <div className="h-8 bg-gray-200 rounded-full flex-1" />
          <div className="h-8 bg-gray-200 rounded-full flex-1" />
        </div>
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────
// EMPTY STATE
// ─────────────────────────────────────────────

const EmptyState = ({ onAdd }) => (
  <div className="text-center py-16 bg-white rounded-2xl shadow-sm border">
    <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
    <p className="text-gray-500 font-medium">No custodians assigned yet</p>
    <p className="text-sm text-gray-400 mt-1">
      Add a staff member to grant Asset Admin access
    </p>
    <button className="btn btn-admin-primary mt-4" onClick={onAdd}>
      <Plus className="w-4 h-4" />
      Add Custodian
    </button>
  </div>
);

// ─────────────────────────────────────────────
// CUSTODIAN CARD (grid mode)
// ─────────────────────────────────────────────

const CustodianCard = ({ custodian, onEdit, onRemove }) => (
  <div className="bg-white rounded-2xl shadow-sm border p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
        <span className="text-sm font-semibold text-red-600">
          {getInitials(custodian.first_name, custodian.last_name)}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{custodian.full_name}</p>
        <p className="text-xs text-gray-500 font-mono">{custodian.staff_id}</p>
        {custodian.department && (
          <p className="text-xs text-gray-400 truncate">{custodian.department}</p>
        )}
      </div>
    </div>

    {custodian.categories.length > 0 ? (
      <div className="flex flex-wrap content-start gap-1.5 flex-1">
        {custodian.categories.map((cat) => (
          <CategoryChip key={cat.id} category={cat} />
        ))}
      </div>
    ) : (
      <p className="text-xs text-gray-400 italic flex-1">No categories assigned</p>
    )}

    <div className="card-action-footer btn-row-spread">
      <button
        onClick={() => onEdit(custodian)}
        className="btn btn-sm btn-admin-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
      >
        <Edit2 className="w-3.5 h-3.5" />
        Edit
      </button>
      <button
        onClick={() => onRemove(custodian)}
        className="btn btn-sm btn-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
      >
        <UserMinus className="w-3.5 h-3.5" />
        Remove
      </button>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// TABLE ROW (list mode)
// ─────────────────────────────────────────────

const CustodianTableRow = ({ custodian, onEdit, onRemove }) => (
  <tr className="table-row">
    <td className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-semibold text-red-600">
            {getInitials(custodian.first_name, custodian.last_name)}
          </span>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">{custodian.full_name}</p>
          <p className="text-xs text-gray-400 font-mono">{custodian.staff_id}</p>
        </div>
      </div>
    </td>
    <td className="px-4 py-3">
      <p className="text-sm text-gray-600">{custodian.department || '—'}</p>
    </td>
    <td className="px-4 py-3">
      <div className="flex flex-wrap gap-1.5">
        {custodian.categories.length > 0
          ? custodian.categories.map((cat) => <CategoryChip key={cat.id} category={cat} />)
          : <span className="text-sm text-gray-400">—</span>
        }
      </div>
    </td>
    <td className="px-4 py-3">
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => onEdit(custodian)}
          className="icon-button-admin"
          title="Edit categories"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => onRemove(custodian)}
          className="icon-button-danger"
          title="Remove custodian"
        >
          <UserMinus className="w-4 h-4" />
        </button>
      </div>
    </td>
  </tr>
);

// ─────────────────────────────────────────────
// CUSTODIAN MODAL (add / edit)
// ─────────────────────────────────────────────

const CustodianModal = ({ isOpen, onClose, editingCustodian, allCategories, onSaved }) => {
  const isMobile = useIsMobile();
  const [staffSearch, setStaffSearch] = useState('');
  const [staffResults, setStaffResults] = useState([]);
  const [staffSearchLoading, setStaffSearchLoading] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [showReloginWarning, setShowReloginWarning] = useState(false);
  const searchTimerRef = useRef(null);

  const updateMutation = useUpdateCustodianCategories();

  // Populate from editingCustodian when modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (editingCustodian) {
      setSelectedStaff({
        staff_id: editingCustodian.staff_id,
        first_name: editingCustodian.first_name,
        last_name: editingCustodian.last_name,
        full_name: editingCustodian.full_name,
        department: editingCustodian.department,
      });
      setSelectedCategoryIds(editingCustodian.categories.map((c) => c.id));
    } else {
      setSelectedStaff(null);
      setSelectedCategoryIds([]);
    }
    setStaffSearch('');
    setStaffResults([]);
    setShowReloginWarning(false);
  }, [isOpen, editingCustodian]);

  // Debounced staff search
  useEffect(() => {
    if (!staffSearch.trim() || editingCustodian) {
      setStaffResults([]);
      return;
    }
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setStaffSearchLoading(true);
      try {
        const res = await staffAPI.getStaffList({ search: staffSearch, page_size: 8 });
        const results = res.data?.results || res.data || [];
        setStaffResults(results);
      } catch {
        setStaffResults([]);
      } finally {
        setStaffSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [staffSearch, editingCustodian]);

  const toggleCategory = (catId) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(catId) ? prev.filter((id) => id !== catId) : [...prev, catId]
    );
  };

  const handleSave = async () => {
    if (!selectedStaff) {
      toast.error('Please select a staff member.');
      return;
    }
    try {
      await updateMutation.mutateAsync({
        staffId: selectedStaff.staff_id,
        categoryIds: selectedCategoryIds,
      });
      toast.success(`Custodian access updated for ${selectedStaff.full_name}`);
      setShowReloginWarning(true);
      onSaved?.();
    } catch {
      // error toast handled by mutation's onError
    }
  };

  const isEdit = !!editingCustodian;
  const canSave = !!selectedStaff;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      theme="admin"
      contentClassName="space-y-5"
      customHeader={
        <div className="modal-header">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Shield className="w-4 h-4 text-red-600" />
            </div>
            <h3 className="modal-title truncate">{isEdit ? 'Edit Custodian' : 'Add Custodian'}</h3>
          </div>
          <button type="button" onClick={onClose} className="modal-close-admin" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
      }
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-admin-outline"
          >
            {showReloginWarning ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave || updateMutation.isPending}
            className="btn btn-admin-primary"
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </>
      }
    >

            {/* Staff search / selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Staff Member
              </label>

              {isEdit ? (
                /* Editing — show read-only staff pill */
                <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-semibold text-red-600">
                      {getInitials(selectedStaff?.first_name, selectedStaff?.last_name)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{selectedStaff?.full_name}</p>
                    <p className="text-xs text-gray-500 font-mono">{selectedStaff?.staff_id}</p>
                  </div>
                </div>
              ) : selectedStaff ? (
                /* Staff selected — show chip with clear button */
                <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-semibold text-red-600">
                      {getInitials(selectedStaff.first_name, selectedStaff.last_name)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{selectedStaff.full_name}</p>
                    <p className="text-xs text-gray-500 font-mono">{selectedStaff.staff_id}</p>
                  </div>
                  <button
                    onClick={() => { setSelectedStaff(null); setStaffSearch(''); }}
                    className="p-1 text-gray-400 hover:text-red-600 rounded-full transition-colors flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                /* Search input */
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={staffSearch}
                    onChange={(e) => setStaffSearch(e.target.value)}
                    placeholder="Search by name or staff ID..."
                    className="input-admin pl-10 w-full"
                    autoFocus={!isMobile}
                  />
                  {staffSearchLoading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                  )}
                </div>
              )}

              {/* Search results dropdown */}
              {!selectedStaff && staffResults.length > 0 && (
                <div className="mt-1 border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  {staffResults.map((staff) => (
                    <button
                      key={staff.staff_id}
                      onClick={() => {
                        setSelectedStaff(staff);
                        setStaffSearch('');
                        setStaffResults([]);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-red-50 transition-colors text-left border-b last:border-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-400"
                    >
                      <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-gray-600">
                          {getInitials(staff.first_name, staff.last_name)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {staff.first_name} {staff.last_name}
                        </p>
                        <p className="text-xs text-gray-500">{staff.staff_id} · {staff.department?.department_name || staff.department || ''}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Category selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Asset Categories
                <span className="text-xs text-gray-400 font-normal ml-1">(select at least one)</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(allCategories || []).filter((c) => c.is_active).map((cat) => {
                  const isSelected = selectedCategoryIds.includes(cat.id);
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => toggleCategory(cat.id)}
                      className={`flex items-center gap-2 p-3 rounded-xl border text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 ${
                        isSelected
                          ? 'bg-red-50 border-red-400 text-red-700'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-red-50 hover:border-red-300'
                      }`}
                    >
                      {isSelected
                        ? <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                        : <div className="w-4 h-4 border-2 border-gray-300 rounded-full flex-shrink-0" />
                      }
                      <div className="flex items-center gap-1.5 min-w-0">
                        <CategoryIcon code={cat.code} className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="text-xs font-medium truncate">{cat.name}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Re-login warning — shown only after first successful save */}
            {showReloginWarning && selectedStaff && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-700">
                  <strong>{selectedStaff.full_name}</strong> must log out and back in for Asset
                  Admin portal access to take effect.
                </p>
              </div>
            )}
    </Modal>
  );
};

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────

const AssetAdminCustodians = () => {
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useViewMode();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCustodian, setEditingCustodian] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  const { data: custodians = [], isLoading } = useCustodians();
  const { data: categoriesData } = useAssetCategories();
  const allCategories = categoriesData?.results || categoriesData || [];
  const updateMutation = useUpdateCustodianCategories();

  const filtered = useMemo(() => {
    if (!search.trim()) return custodians;
    const q = search.toLowerCase();
    return custodians.filter(
      (c) =>
        c.full_name.toLowerCase().includes(q) ||
        c.staff_id.toLowerCase().includes(q) ||
        (c.department || '').toLowerCase().includes(q)
    );
  }, [custodians, search]);

  const openAdd = useCallback(() => {
    setEditingCustodian(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((custodian) => {
    setEditingCustodian(custodian);
    setModalOpen(true);
  }, []);

  const handleRemove = async () => {
    if (!removeTarget) return;
    await updateMutation.mutateAsync({ staffId: removeTarget.staff_id, categoryIds: [] });
    toast.success(`Removed custodian access for ${removeTarget.full_name}`);
    setRemoveTarget(null);
  };

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">Custodian Management</h1>
            <p className="text-xs sm:text-sm text-gray-500">
              Manage which staff members have Asset Admin portal access
            </p>
          </div>
        </div>
        <button
          onClick={openAdd}
          className="btn btn-admin-primary w-full sm:w-auto flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          Add Custodian
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        {/* Left: Search */}
        <DebouncedSearchInput
          value={search}
          onSearch={setSearch}
          placeholder="Search by name or staff ID…"
          className="max-w-xl"
        />

        {/* Right: Count → View toggle */}
        <div className="flex items-center gap-2">
          <CountBadge count={filtered.length} theme="admin" size="md" />
          <PillToggle
            value={viewMode}
            onChange={setViewMode}
            options={[
              { id: 'grid', label: 'Grid view', icon: Grid3X3 },
              { id: 'list', label: 'List view', icon: List },
            ]}
            variant="icon"
            theme="admin"
            ariaLabel="View mode"
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <GridSkeleton />
      ) : filtered.length === 0 && !search ? (
        <EmptyState onAdd={openAdd} />
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl shadow-sm border">
          <Search className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">No custodians match &ldquo;{search}&rdquo;</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <CustodianCard key={c.staff_id} custodian={c} onEdit={openEdit} onRemove={setRemoveTarget} />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="table-header border-b">
                <tr>
                  <th className="table-header-cell">Staff Member</th>
                  <th className="table-header-cell">Department</th>
                  <th className="table-header-cell">Managed Categories</th>
                  <th className="table-header-cell text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((c) => (
                  <CustodianTableRow
                    key={c.staff_id}
                    custodian={c}
                    onEdit={openEdit}
                    onRemove={setRemoveTarget}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit modal */}
      <CustodianModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        editingCustodian={editingCustodian}
        allCategories={allCategories}
        onSaved={() => {
          /* modal stays open to show re-login warning; user closes manually */
        }}
      />

      {/* Remove confirmation */}
      <ConfirmModal
        isOpen={!!removeTarget}
        title="Remove Custodian Access"
        message={`Remove all asset custodian access for ${removeTarget?.full_name}? They will no longer be able to manage assets in the portal.`}
        confirmLabel="Remove Access"
        variant="danger"
        theme="admin"
        isLoading={updateMutation.isPending}
        onConfirm={handleRemove}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
};

// ─────────────────────────────────────────────
// LOCAL HOOK — view mode persistence
// ─────────────────────────────────────────────

const VALID_VIEW_MODES = ['grid', 'list'];

function useViewMode() {
  return usePersistence('asset-custodians-view', 'grid', VALID_VIEW_MODES);
}

export default AssetAdminCustodians;
